import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchCareLink360Summary, triggerCareLink360Sync } from "../utils/api";

const summaryKey = (clientId) => ["carelink360-summary", clientId || null];

// While a pull is running the panel is stale by definition, so it re-checks on
// this cadence until the backend reports the run finished.
const SYNC_POLL_MS = 6 * 1000;

/**
 * CareLink360 activity summary for one client's Snapshot "CareLink 360" section.
 * Returns { linked, client, totalRecords, lastActivityAt, reports, sync } — or
 * { linked: false } when the client has no CareLink360 counterpart.
 *
 * Polls itself while `sync.inProgress` is true, so a sync started from the panel
 * lands on screen without the user reloading.
 */
export function useCareLink360Summary(clientId, { enabled = true } = {}) {
  return useQuery({
    queryKey: summaryKey(clientId),
    queryFn: () => fetchCareLink360Summary(clientId),
    enabled: !!clientId && enabled,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) =>
      query.state.data?.sync?.inProgress ? SYNC_POLL_MS : false,
  });
}

/**
 * Queues a CareLink360 refresh for the client's agency.
 *
 * The request answers as soon as the pull is queued, not when it finishes — the
 * summary query above takes over and polls for the result. Rejections carry the
 * backend's `{ status, message, cooldownSeconds }` on `error.response.data`
 * (429 = cooldown, 409 = paused / not connected, 404 = not linked).
 */
export function useCareLink360Sync(clientId) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => triggerCareLink360Sync(clientId),
    onSuccess: (result) => {
      // Seed the sync state from the response so polling starts immediately
      // instead of waiting for a refetch to discover the run.
      queryClient.setQueryData(summaryKey(clientId), (previous) =>
        previous
          ? { ...previous, sync: { ...previous.sync, ...result, canSync: false } }
          : previous,
      );
      queryClient.invalidateQueries({ queryKey: summaryKey(clientId) });
    },
  });
}

export default useCareLink360Summary;
